import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/contexts/DialogContext';
import { useToast } from '@/contexts/ToastContext';
import bytedanceModelIcon from '@/assets/images/pricing-models/bytedance.png';
import claudeModelIcon from '@/assets/images/pricing-models/claude.svg';
import nanoBananaModelIcon from '@/assets/images/pricing-models/nano-banana.svg';
import { useTranslation } from '@client-app/i18n';
import { billingService, Plan, SubscriptionSummaryCardState, WorkspaceSubscriptionSummary } from '@common/api/billingService';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Buildings } from '@phosphor-icons/react';
import ContactSalesModal from './ContactSalesModal';
import PlanComparison from './PlanComparison';
import PricingCard, { type PlanData, type IncludedAiModel, type PlanFeatureGroups } from './PricingCard';
import PricingTierSelect from './PricingTierSelect';
import {
  formatScheduledEffectiveDate,
  resolveScheduledPlanBubbleState,
  resolveScheduledPlanButtonState,
} from './scheduledPlanChangeUtils';
import { resolveExpiredPlanCardButtonText, resolveSummaryCardState } from './pricingStatusUtils';
import {
  getTrialPlanCardAction,
  getTrialStartSubscriptionCardState,
  isImmediateTrialActivationSelection,
  resolveTrialActivationCheckoutResult,
  resolveTrialWorkspaceState,
  shouldRequireCheckoutForTrialPlanSelection,
  shouldShowActivatePaidAction,
} from './trialActivationUtils';
import { buildCheckoutReturnUrls } from './paymentUrlUtils';
import { buildPaymentReturnUrl, PAYMENT_RETURN_UPGRADE_PATH } from '@/pages/PaymentReturn/paymentReturnFlow';
import { resolvePlanChangeIntent } from './planChangeIntent';
import { resolvePricingEntryDefaults } from './pricingEntryDefaults';
import { isSelectablePlanCode, isSoloPlanCode, isTeamPlanCode, SelectablePlanCode, BillingCycle } from './teamOfferSelection';
import {
  DEFAULT_SOLO_TIER_ID,
  DEFAULT_TEAM_TIER_ID,
  buildPricingTierCatalogFromPlanComparison,
  getPricingTier,
  getPricingTierForPlanCode,
  getPricingTierLimits,
  getPricingTierPrice,
  isCustomPricingTierLimit,
  isUnlimitedPricingTierLimit,
  resolveConfiguredPlanEstimatedOperations,
  resolveConfiguredPlanWorkUnits,
  resolveFrontendPricingTierActionLabel,
  resolvePricingTierWorkUnits,
  resolveSelectedSoloTierId,
  type PricingTierCatalog,
  type PricingTierId,
  type PricingTierOption,
} from './pricingTierOptions';
import { captureSentryError, logSentryEvent } from '@/lib/sentry';

export interface UserPlanInfo {
  planCode?: string;
  billingCycle?: 'monthly' | 'yearly';
  status?: string;
  currentPeriodEnd?: string;
  trialEndAt?: string;
}

type PlanSelectorMode = 'upgrade';
type EffectiveMode = 'next_cycle' | 'immediate' | null;
type ShopifyCancellationCopyState = 'pending_payment' | 'active_paid';

interface TrialActivationStatus {
  loaded: boolean;
  canActivatePaidNow: boolean;
  isInTrial: boolean;
  hasSetupCard: boolean;
}

interface ScheduledPlanChangeState {
  planCode: SelectablePlanCode;
  billingCycle: BillingCycle;
}

interface ScheduledChangeStatus {
  changeType?: string | null;
  effectiveMode?: string | null;
  effectiveAt?: string | null;
  fromPlanCode?: string | null;
  fromPlanName?: string | null;
  fromBillingCycle?: string | null;
  toPlanCode?: string | null;
  toPlanName?: string | null;
  toBillingCycle?: string | null;
}

interface PlanSelectorProps {
  mode?: PlanSelectorMode;
  userPlan?: UserPlanInfo;
  disabled?: boolean;
  className?: string;
  workspaceId: string;
  preferredTeamOfferCode?: SelectablePlanCode | null;
}

const normalizeCode = (code?: string | null) => (code || '').toUpperCase();
const normalizeCycle = (cycle?: string | null): BillingCycle => (cycle === 'yearly' ? 'yearly' : 'monthly');
const normalizeProviderToken = (provider?: string | null) =>
  String(provider || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
const isShopifyBillingProvider = (...providers: Array<string | null | undefined>) => providers.some((provider) => normalizeProviderToken(provider).includes('shopify'));
const normalizeStateToken = (value?: string | null) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
const getSummaryToken = (summary: WorkspaceSubscriptionSummary | null | undefined, keys: Array<keyof WorkspaceSubscriptionSummary>) => {
  for (const key of keys) {
    const token = normalizeStateToken(summary?.[key] as string | null | undefined);
    if (token) return token;
  }
  return '';
};
const getSummaryBoolean = (summary: WorkspaceSubscriptionSummary | null | undefined, keys: Array<keyof WorkspaceSubscriptionSummary>) => keys.some((key) => summary?.[key] === true);
const hasPendingPaymentMeaning = (value: string) => value.includes('pending') || value.includes('awaiting') || value.includes('unpaid') || value.includes('not_received');
const resolveShopifyCancellationCopyState = (summary: WorkspaceSubscriptionSummary | null | undefined): ShopifyCancellationCopyState => {
  const paymentStatus = getSummaryToken(summary, ['shopifyPaymentStatus', 'shopify_payment_status', 'paymentStatus', 'payment_status']);
  const reasonCode = getSummaryToken(summary, ['reasonCode', 'reason_code']);
  const refreshCadence = getSummaryToken(summary, ['planCreditRefreshCadence', 'plan_credit_refresh_cadence']);
  const explicitPaymentPending = getSummaryBoolean(summary, ['shopifyPaymentPending', 'shopify_payment_pending', 'paymentPending', 'payment_pending', 'isPaymentPending', 'is_payment_pending']);

  return explicitPaymentPending || hasPendingPaymentMeaning(paymentStatus) || hasPendingPaymentMeaning(reasonCode) || hasPendingPaymentMeaning(refreshCadence) ? 'pending_payment' : 'active_paid';
};
const toNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const fallbackPlan = ({
  code,
  name,
  tier,
  pointsQuota,
  monthlyAmount,
  yearlyAmount,
  yearlyDisplayMonthlyAmount,
  isDefault = false,
}: {
  code: SelectablePlanCode;
  name: string;
  tier: string;
  pointsQuota: number;
  monthlyAmount: number;
  yearlyAmount: number;
  yearlyDisplayMonthlyAmount: number;
  isDefault?: boolean;
}): Plan[] => [
  {
    id: `__fallback_${code.toLowerCase()}_monthly__`,
    code,
    name,
    tier,
    pointsQuota,
    priceCents: monthlyAmount,
    chargeAmountCents: monthlyAmount,
    displayMonthlyAmountCents: monthlyAmount,
    currency: 'USD',
    billingCycle: 'monthly',
    isActive: true,
    isDefault,
    createdAt: '',
  },
  {
    id: `__fallback_${code.toLowerCase()}_yearly__`,
    code,
    name,
    tier,
    pointsQuota,
    priceCents: yearlyAmount,
    chargeAmountCents: yearlyAmount,
    displayMonthlyAmountCents: yearlyDisplayMonthlyAmount,
    currency: 'USD',
    billingCycle: 'yearly',
    isActive: true,
    isDefault: false,
    createdAt: '',
  },
];

const FALLBACK_PLANS: Plan[] = [
  ...fallbackPlan({ code: 'SOLO_199', name: 'Solo', tier: 'SOLO_199', pointsQuota: 2780, monthlyAmount: 199, yearlyAmount: 2150, yearlyDisplayMonthlyAmount: 179 }),
  ...fallbackPlan({ code: 'SOLO_399', name: 'Solo', tier: 'SOLO_399', pointsQuota: 6000, monthlyAmount: 399, yearlyAmount: 4310, yearlyDisplayMonthlyAmount: 359 }),
  ...fallbackPlan({ code: 'BASIC', name: 'Solo', tier: 'BASIC', pointsQuota: 11000, monthlyAmount: 699, yearlyAmount: 7548, yearlyDisplayMonthlyAmount: 629, isDefault: true }),
  ...fallbackPlan({ code: 'SOLO_999', name: 'Solo', tier: 'SOLO_999', pointsQuota: 15000, monthlyAmount: 999, yearlyAmount: 10790, yearlyDisplayMonthlyAmount: 899 }),
  ...fallbackPlan({ code: 'SOLO_1299', name: 'Solo', tier: 'SOLO_1299', pointsQuota: 20000, monthlyAmount: 1299, yearlyAmount: 14030, yearlyDisplayMonthlyAmount: 1169 }),
  ...fallbackPlan({ code: 'TEAM_1499', name: 'Team', tier: 'TEAM_1499', pointsQuota: 22500, monthlyAmount: 1499, yearlyAmount: 16190, yearlyDisplayMonthlyAmount: 1349 }),
  ...fallbackPlan({ code: 'TEAM_2999', name: 'Team', tier: 'TEAM_2999', pointsQuota: 45000, monthlyAmount: 2999, yearlyAmount: 32390, yearlyDisplayMonthlyAmount: 2699 }),
  ...fallbackPlan({ code: 'PRO', name: 'Team', tier: 'PRO', pointsQuota: 75000, monthlyAmount: 4999, yearlyAmount: 53988, yearlyDisplayMonthlyAmount: 4499 }),
  ...fallbackPlan({ code: 'TEAM_9999', name: 'Team', tier: 'TEAM_9999', pointsQuota: 150000, monthlyAmount: 9999, yearlyAmount: 107990, yearlyDisplayMonthlyAmount: 8999 }),
  ...fallbackPlan({ code: 'TEAM_49999', name: 'Team', tier: 'TEAM_49999', pointsQuota: 750000, monthlyAmount: 49999, yearlyAmount: 539990, yearlyDisplayMonthlyAmount: 44999 }),
];
const isFallbackPlan = (plan?: Plan | null): boolean => Boolean(plan?.id?.startsWith('__fallback__'));
const findPlanByCodeAndCycle = (planList: Plan[], planCode: SelectablePlanCode, cycle: BillingCycle): Plan | undefined => planList.find((plan) => normalizeCode(plan.code) === planCode && normalizeCycle(plan.billingCycle) === cycle);
const resolveEffectiveMode = (response: any): EffectiveMode => {
  if (!response || typeof response !== 'object') return null;
  const rawMode = response.effectiveMode || response.effective_mode || response.mode || response.change?.effectiveMode || response.change?.effective_mode || response.subscription?.effectiveMode || response.subscription?.effective_mode;
  if (typeof rawMode !== 'string') return null;
  const normalized = rawMode.toLowerCase();
  if (normalized === 'next_cycle' || normalized === 'next-cycle') return 'next_cycle';
  if (normalized === 'immediate' || normalized === 'now' || normalized === 'instant') return 'immediate';
  return null;
};

const toScheduledChangeStatus = (scheduledChange?: any): ScheduledChangeStatus | null => {
  if (!scheduledChange) {
    return null;
  }

  return {
    changeType: scheduledChange.changeType ?? null,
    effectiveMode: scheduledChange.effectiveMode ?? null,
    effectiveAt: scheduledChange.effectiveAt ?? null,
    fromPlanCode: scheduledChange.fromPlanCode ?? null,
    fromPlanName: scheduledChange.fromPlanName ?? null,
    fromBillingCycle: scheduledChange.fromBillingCycle ?? null,
    toPlanCode: scheduledChange.toPlanCode ?? null,
    toPlanName: scheduledChange.toPlanName ?? null,
    toBillingCycle: scheduledChange.toBillingCycle ?? null,
  };
};

const PlanSelector: React.FC<PlanSelectorProps> = ({ mode = 'upgrade', userPlan, disabled = false, className, workspaceId, preferredTeamOfferCode = null }) => {
  const { locale, t } = useTranslation();
  const { openSignInModal, user, profile, setProfile, userBilling, refreshWorkspaces } = useAuth();
  const { showToast } = useToast();
  const { showDialog } = useDialog();
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const builtInAiModels = useMemo<IncludedAiModel[]>(() => [
    {
      name: 'Nano Banana 2',
      iconSrc: nanoBananaModelIcon,
      capabilityLabel: t('website.pricing.createImagesWith', { model: 'Nano Banana 2' }),
    },
    {
      name: 'Seedance 2.0',
      iconSrc: bytedanceModelIcon,
      capabilityLabel: t('website.pricing.createVideosWith', { model: 'Seedance 2.0' }),
    },
    {
      name: 'Opus 4.8',
      iconSrc: claudeModelIcon,
      capabilityLabel: t('website.pricing.handleBusinessTasksWith', { model: 'Opus 4.8' }),
    },
  ], [t]);

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly');
  const [plans, setPlans] = useState<Plan[]>(FALLBACK_PLANS);
  const [pricingTierCatalog, setPricingTierCatalog] = useState<PricingTierCatalog>(() => buildPricingTierCatalogFromPlanComparison());
  const [isContactSalesOpen, setIsContactSalesOpen] = useState(false);
  const [loadingPlanName, setLoadingPlanName] = useState<string | undefined>();
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [trialActivationStatus, setTrialActivationStatus] = useState<TrialActivationStatus>({
    loaded: false,
    canActivatePaidNow: false,
    isInTrial: false,
    hasSetupCard: false,
  });
  const [scheduledPlanChange, setScheduledPlanChange] = useState<ScheduledPlanChangeState | null>(null);
  const [scheduledChangeStatus, setScheduledChangeStatus] = useState<ScheduledChangeStatus | null>(null);
  const [selectedSoloTierId, setSelectedSoloTierId] = useState<PricingTierId>(DEFAULT_SOLO_TIER_ID);
  const [hasManualSoloTierSelection, setHasManualSoloTierSelection] = useState(false);
  const [manualTeamTierId, setManualTeamTierId] = useState<PricingTierId | null>(null);
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
  const plansRequestRef = useRef<Promise<Plan[]> | null>(null);
  const getLocalizedPlanName = useCallback(
    (planCode?: string | null, fallbackName?: string | null): string => {
      const normalizedPlanCode = normalizeCode(planCode);
      if (isSoloPlanCode(normalizedPlanCode)) return t('website.pricing.soloName');
      if (isTeamPlanCode(normalizedPlanCode)) return t('website.pricing.teamName');
      if (normalizedPlanCode === 'ENTERPRISE') return t('website.pricing.scaleName');
      return fallbackName || '';
    },
    [t],
  );
  const getLocalizedButtonText = useCallback(
    (buttonText: string): string => {
      switch (buttonText) {
        case 'Contact Sales':
          return t('website.pricing.contactSales');
        case 'Unavailable':
          return t('website.pricing.unavailable');
        case 'Available soon':
          return t('website.pricing.availableSoon');
        case 'Start subscription':
          return t('website.pricing.startSubscription');
        case 'Activate Paid Plan':
          return t('website.pricing.activatePaidPlan');
        case 'Renew':
          return t('website.planText.renew');
        case 'Switch to Solo':
          return t('website.pricing.switchToSolo');
        case 'Switch to Team':
          return t('website.pricing.switchToTeam');
        case 'Switch plan':
          return t('website.pricing.switchPlan');
        case 'Current Plan':
          return t('website.pricing.currentPlan');
        case 'Resubscribe':
          return t('website.pricing.resubscribe');
        case 'Switch to Yearly':
          return t('website.pricing.switchToYearly');
        case 'Switch to Monthly':
          return t('website.pricing.switchToMonthly');
        case 'Upgrade to Team':
          return t('website.pricing.upgradeToTeam');
        case 'Upgrade':
          return t('website.pricing.upgrade');
        case 'Downgrade':
          return t('website.pricing.downgrade');
        case 'Downgrade scheduled':
          return t('website.pricing.downgradeScheduled');
        default:
          return buttonText;
      }
    },
    [t],
  );
  const getPricingCardLoadingName = useCallback((planCode: SelectablePlanCode): string => (isTeamPlanCode(planCode) ? t('website.pricing.teamName') : t('website.pricing.soloName')), [t]);

  const currentPlanCode = normalizeCode(userBilling.summary?.planCode || userPlan?.planCode);
  const userBillingCycle = normalizeCycle(userBilling.summary?.billingCycle || userPlan?.billingCycle);
  const subscriptionStatus = String(userBilling.summary?.status || userPlan?.status || '').toLowerCase();
  const fallbackTrialWorkspace = Boolean(userBilling.isTrial || subscriptionStatus === 'trial' || userBilling.summary?.isTrial);
  const isTrialWorkspace = resolveTrialWorkspaceState({
    setupStatusIsInTrial: trialActivationStatus.isInTrial,
    billingStateIsTrial: fallbackTrialWorkspace,
  });
  const isExpired = Boolean(userBilling.isExpired || subscriptionStatus === 'expired' || subscriptionStatus === 'canceled');
  const isCanceled = userBilling.isCanceled && !isExpired;
  const isShopifyBilling = isShopifyBillingProvider(userBilling.summary?.provider, userBilling.summary?.billingProvider, userBilling.summary?.paymentProvider, userBilling.userPlan?.provider) || Boolean(window.isShopify);
  const isShopifySubscriptionCanceled = Boolean(isCanceled || (isShopifyBilling && subscriptionStatus === 'canceled'));
  const shopifyCancellationCopyState = resolveShopifyCancellationCopyState(userBilling.summary);
  const effectiveCurrentPlanCode = currentPlanCode;
  const effectiveCurrentBillingCycle = userBillingCycle;
  const shouldUseTrialUi = Boolean(isTrialWorkspace);
  const pricingEntryDefaults = resolvePricingEntryDefaults({
    hasCurrentPlan: Boolean(currentPlanCode),
    isTrial: shouldUseTrialUi,
    isExpired,
  });
  const shouldBypassSummaryCardState = Boolean(shouldUseTrialUi || isExpired);
  const requiresCheckoutForTrialSelection = Boolean(
    trialActivationStatus.loaded &&
    shouldRequireCheckoutForTrialPlanSelection({
      isInTrial: isTrialWorkspace,
      hasSetupCard: trialActivationStatus.hasSetupCard,
    }),
  );
  const getDynamicPricingTier = useCallback(
    (group: 'solo' | 'team', tierId: PricingTierId): PricingTierOption => {
      const tiers = group === 'solo' ? pricingTierCatalog.soloTiers : pricingTierCatalog.teamTiers;
      return tiers.find((tier) => tier.id === tierId) ?? getPricingTier(group, tierId);
    },
    [pricingTierCatalog.soloTiers, pricingTierCatalog.teamTiers],
  );
  const getDynamicPricingTierForPlanCode = useCallback(
    (group: 'solo' | 'team', planCode?: string | null): PricingTierOption | null => {
      const normalizedPlanCode = String(planCode || '').toUpperCase();
      const tiers = group === 'solo' ? pricingTierCatalog.soloTiers : pricingTierCatalog.teamTiers;
      return tiers.find((tier) => tier.planCode === normalizedPlanCode) ?? getPricingTierForPlanCode(group, planCode);
    },
    [pricingTierCatalog.soloTiers, pricingTierCatalog.teamTiers],
  );
  const selectedSoloTier = getDynamicPricingTier(
    'solo',
    resolveSelectedSoloTierId({
      entryDefaultTierId: pricingEntryDefaults.soloTierId,
      hasManualSelection: hasManualSoloTierSelection,
      selectedTierId: selectedSoloTierId,
      currentPlanCode,
    }),
  );
  const preferredTeamTier = getDynamicPricingTierForPlanCode('team', preferredTeamOfferCode);
  const currentTeamTier = getDynamicPricingTierForPlanCode('team', currentPlanCode);
  const selectedTeamTier = manualTeamTierId
    ? getDynamicPricingTier('team', manualTeamTierId)
    : pricingEntryDefaults.teamTierId
      ? getDynamicPricingTier('team', pricingEntryDefaults.teamTierId)
      : preferredTeamTier ?? currentTeamTier ?? getDynamicPricingTier('team', DEFAULT_TEAM_TIER_ID);

  const shouldAllowImmediateTrialActivation = (planCode: SelectablePlanCode, cycle: BillingCycle): boolean => {
    if (!shouldUseTrialUi || !trialActivationStatus.isInTrial) {
      return false;
    }

    const selectedPlanCode = scheduledPlanChange?.planCode ?? currentPlanCode;
    const selectedCycle = scheduledPlanChange?.billingCycle ?? userBillingCycle;

    if (selectedPlanCode !== planCode || selectedCycle !== cycle) {
      return false;
    }

    return isImmediateTrialActivationSelection({
      currentPlanCode: currentPlanCode as SelectablePlanCode,
      currentCycle: userBillingCycle,
      selectedPlanCode,
      selectedCycle,
    });
  };

  useEffect(() => {
    setBillingCycle(pricingEntryDefaults.billingCycle ?? userBillingCycle);
  }, [pricingEntryDefaults.billingCycle, userBillingCycle]);

  useEffect(() => {
    if (!scheduledPlanChange) return;
    if (!shouldUseTrialUi) {
      setScheduledPlanChange(null);
      return;
    }
    if (scheduledPlanChange.planCode === currentPlanCode && scheduledPlanChange.billingCycle === userBillingCycle) {
      setScheduledPlanChange(null);
    }
  }, [scheduledPlanChange, shouldUseTrialUi, currentPlanCode, userBillingCycle]);

  useEffect(() => {
    const backendStillTrial = subscriptionStatus === 'trial' || Boolean(userBilling.summary?.isTrial);
    if (backendStillTrial || (!trialActivationStatus.isInTrial && !trialActivationStatus.canActivatePaidNow)) {
      return;
    }
    setTrialActivationStatus((prev) => ({
      ...prev,
      loaded: true,
      canActivatePaidNow: false,
      isInTrial: false,
    }));
  }, [subscriptionStatus, userBilling.summary?.isTrial, trialActivationStatus.isInTrial, trialActivationStatus.canActivatePaidNow]);

  const fetchPlans = useCallback(async (): Promise<Plan[]> => {
    if (plansRequestRef.current) {
      return plansRequestRef.current;
    }

    const request = (async () => {
      setFetchingPlans(true);
      try {
        const response = await billingService.getPlans({
          headers: { 'X-Workspace-Id': workspaceId },
        });
        const nextPlans = Array.isArray(response) && response.length > 0 ? response : FALLBACK_PLANS;
        setPlans(nextPlans);
        return nextPlans;
      } catch (err) {
        console.error('Failed to fetch plans', err);
        return FALLBACK_PLANS;
      } finally {
        setFetchingPlans(false);
        plansRequestRef.current = null;
      }
    })();

    plansRequestRef.current = request;
    return request;
  }, [workspaceId]);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    let cancelled = false;
    const fetchComparisonCatalog = async () => {
      if (!workspaceId) {
        return;
      }
      try {
        const catalog = await billingService.getPlanComparisonCatalog({
          headers: { 'X-Workspace-Id': workspaceId },
        });
        if (!cancelled) {
          setPricingTierCatalog(buildPricingTierCatalogFromPlanComparison(catalog));
        }
      } catch (err) {
        console.error('Failed to fetch plan comparison catalog', err);
      }
    };

    void fetchComparisonCatalog();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    let cancelled = false;
    const fetchTrialStatus = async () => {
      if (!user || mode !== 'upgrade' || !workspaceId) {
        if (!cancelled) {
          setTrialActivationStatus({
            loaded: false,
            canActivatePaidNow: false,
            isInTrial: false,
            hasSetupCard: false,
          });
          setScheduledChangeStatus(null);
        }
        return;
      }
      try {
        const status = await billingService.getSetupCardStatus({
          headers: { 'X-Workspace-Id': workspaceId },
        });
        if (cancelled) return;
        const nextStatus = {
          loaded: true,
          // 试用期允许用户主动立即转正：服务端判定优先，试用状态保留兜底。
          canActivatePaidNow: Boolean(status?.canActivatePaidNow || status?.subscription?.canActivatePaidNow),
          isInTrial: Boolean(status?.subscription?.isInTrial),
          hasSetupCard: Boolean(status?.hasSetupCard),
        };
        if (import.meta.env.DEV) {
          console.info('[Pricing][TrialActivation] setup-card status', {
            workspaceId,
            apiStatus: status,
            resolved: nextStatus,
          });
        }
        setScheduledChangeStatus(toScheduledChangeStatus(status?.scheduledChange));
        setTrialActivationStatus(nextStatus);
      } catch (err) {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          console.error('[Pricing][TrialActivation] setup-card status request failed', err);
        }
        setTrialActivationStatus({
          loaded: true,
          canActivatePaidNow: false,
          isInTrial: false,
          hasSetupCard: false,
        });
        setScheduledChangeStatus(null);
      }
    };
    fetchTrialStatus();
    return () => {
      cancelled = true;
    };
  }, [user, mode, workspaceId, userBilling.summary?.status, userBilling.summary?.displayRemainingMicrousd]);

  const getPlanByCodeAndCycle = (planCode: SelectablePlanCode, cycle: BillingCycle) => {
    return findPlanByCodeAndCycle(plans, planCode, cycle);
  };

  const ensureRealPlan = useCallback(
    async (planCode: SelectablePlanCode, cycle: BillingCycle): Promise<Plan | null> => {
      const existingPlan = getPlanByCodeAndCycle(planCode, cycle);
      if (existingPlan && !isFallbackPlan(existingPlan)) {
        return existingPlan;
      }

      const loadedPlans = await fetchPlans();
      const resolvedPlan = findPlanByCodeAndCycle(loadedPlans, planCode, cycle);

      if (resolvedPlan && !isFallbackPlan(resolvedPlan)) {
        return resolvedPlan;
      }

      return null;
    },
    [fetchPlans, plans, billingCycle],
  );

  const summaryCards = useMemo(() => {
    if (!userBilling.summary?.cards) return [] as SubscriptionSummaryCardState[];
    return Array.isArray(userBilling.summary.cards) ? userBilling.summary.cards : Object.values(userBilling.summary.cards);
  }, [userBilling.summary?.cards]);

  const getSummaryCardState = (planCode: SelectablePlanCode, cycle: BillingCycle) => {
    return resolveSummaryCardState({
      summaryCards,
      planCode,
      cycle,
      effectiveCurrentPlanCode,
      effectiveCurrentBillingCycle,
    });
  };

  const isRenewableCurrentPlanCard = (planCode: SelectablePlanCode, cycle: BillingCycle): boolean => {
    if (isShopifyBilling) {
      return false;
    }
    if (!isCanceled || effectiveCurrentPlanCode !== planCode || effectiveCurrentBillingCycle !== cycle) {
      return false;
    }

    return !resolveScheduledPlanButtonState({
      scheduledChange: scheduledChangeStatus,
      currentPlanCode: effectiveCurrentPlanCode,
      currentBillingCycle: effectiveCurrentBillingCycle,
      planCode,
      billingCycle: cycle,
    });
  };

  const shouldShowActivateForCard = (planCode: SelectablePlanCode, cycle: BillingCycle): boolean => {
    if (!shouldUseTrialUi) {
      return false;
    }
    const selectedPlanCode = scheduledPlanChange?.planCode ?? currentPlanCode;
    const selectedCycle = scheduledPlanChange?.billingCycle ?? userBillingCycle;
    const isCurrentPlan = selectedPlanCode === planCode;
    const isCurrentCycle = selectedCycle === cycle;

    return shouldShowActivatePaidAction({
      isCurrentPlan,
      isCurrentCycle,
      canActivatePaidNow: isCurrentPlan && isCurrentCycle && shouldAllowImmediateTrialActivation(planCode, cycle),
    });
  };

  const getTierDisplayedPrice = (tier: PricingTierOption): number => (
    getPricingTierPrice(tier, billingCycle)
  );

  const getTierWorkUnits = (tier: PricingTierOption): number | null => {
    const configuredWorkUnits = tier.planCode
      ? resolveConfiguredPlanWorkUnits(getPlanByCodeAndCycle(tier.planCode, billingCycle))
      : null;
    return resolvePricingTierWorkUnits(tier, configuredWorkUnits);
  };

  const getTierSelectorOptions = (tiers: readonly PricingTierOption[]) => tiers.map((tier) => {
    const workUnits = getTierWorkUnits(tier);
    const limits = getPricingTierLimits(tier, billingCycle);

    const userLimitLabel = isUnlimitedPricingTierLimit(limits.workspaceUsers)
      ? t('website.pricing.unlimitedUsersShort')
      : limits.workspaceUsers === 1
        ? t('website.pricing.oneUserShort')
        : t('website.pricing.usersShort', { value: limits.workspaceUsers });

    const storeLimitLabel = isCustomPricingTierLimit(limits.storeConnections)
      ? t('website.pricing.customStoresShort')
      : isUnlimitedPricingTierLimit(limits.storeConnections)
      ? t('website.pricing.unlimitedStoresShort')
      : limits.storeConnections === 1
        ? t('website.pricing.oneStoreShort')
        : t('website.pricing.storesShort', { value: limits.storeConnections });

    const secondaryLabel = `${storeLimitLabel}, ${userLimitLabel}`;

    return {
      id: tier.id,
      primaryLabel: workUnits === null
        ? t('website.pricing.workUnitsToBeConfirmed')
        : t('website.pricing.workUnitsPerMonth', { value: numberFormatter.format(workUnits) }),
      secondaryLabel,
    };
  });

  const getTierWorkUnitsFeature = (tier: PricingTierOption): string => {
    const workUnits = getTierWorkUnits(tier);
    if (workUnits === null) {
      return tier.group === 'team'
        ? t('website.pricing.sharedWorkUnitsFeatureTbd')
        : t('website.pricing.workUnitsFeatureTbd');
    }

    return tier.group === 'team'
      ? t('website.pricing.sharedWorkUnitsFeature', { value: numberFormatter.format(workUnits) })
      : t('website.pricing.workUnitsFeature', { value: numberFormatter.format(workUnits) });
  };

  const getTierFeatureGroups = (tier: PricingTierOption): PlanFeatureGroups => {
    const limits = getPricingTierLimits(tier, billingCycle);
    const userFeature = tier.group === 'solo'
      ? t('website.pricing.soloFeatureOne')
      : isUnlimitedPricingTierLimit(limits.workspaceUsers)
        ? t('website.pricing.unlimitedWorkspaceUsersIncluded')
        : t('website.pricing.workspaceUsersIncluded', { value: numberFormatter.format(limits.workspaceUsers) });
    const storeFeature = tier.group === 'solo'
      ? t('website.pricing.soloFeatureStoreLimit')
      : isCustomPricingTierLimit(limits.storeConnections)
        ? t('website.pricing.customStoreConnectionsIncluded')
      : isUnlimitedPricingTierLimit(limits.storeConnections)
        ? t('website.pricing.unlimitedStoreConnectionsIncluded')
        : t('website.pricing.storeConnectionsIncluded', { value: numberFormatter.format(limits.storeConnections) });

    return {
      workspace: [
        { id: 'workspace-users', label: userFeature },
        { id: 'store-connections', label: storeFeature },
      ],
      workUnits: [
        { id: 'monthly-work-units', label: getTierWorkUnitsFeature(tier) },
        { id: 'work-unit-top-ups', label: t('website.pricing.creditTopUps') },
      ],
      trainingAndIntegrations: [
        { id: 'recorded-courses', label: t('website.pricing.recordedCoursesIncluded') },
        ...(tier.group === 'team'
          ? [
            {
              id: 'weekly-live-sessions',
              label: t('website.pricing.weeklyLiveSessionsIncluded'),
              badge: 'team-exclusive' as const,
            },
            {
              id: 'onsite-training',
              label: t('website.pricing.onsiteTrainingIncluded'),
              badge: 'team-exclusive' as const,
            },
          ]
          : []),
      ],
    };
  };

  const getTierEstimatedCapacity = (tier: PricingTierOption): string => {
    const configuredPlan = tier.planCode
      ? getPlanByCodeAndCycle(tier.planCode, billingCycle)
      : null;
    const { min, max } = resolveConfiguredPlanEstimatedOperations(configuredPlan)
      ?? tier.estimatedMonthlyOperations;
    const translationKey = tier.group === 'team'
      ? 'website.pricing.estimatedTeamOperationsPerMonth'
      : 'website.pricing.estimatedSoloOperationsPerMonth';

    return t(translationKey, {
      min: numberFormatter.format(min),
      max: numberFormatter.format(max),
    });
  };

  const getYearlyCharge = (planCode: SelectablePlanCode): number | null => {
    const yearlyPlan = getPlanByCodeAndCycle(planCode, 'yearly');
    if (!yearlyPlan) return null;
    return toNumberOrNull(yearlyPlan.chargeAmountCents) ?? toNumberOrNull(yearlyPlan.priceCents);
  };

  const getPlanDisplayName = (planCode?: string | null, cycle?: BillingCycle | string | null): string | null => {
    const normalizedCode = normalizeCode(planCode);
    if (!isSelectablePlanCode(normalizedCode)) {
      return null;
    }
    const normalizedCycle = normalizeCycle(cycle);
    return getPlanByCodeAndCycle(normalizedCode as SelectablePlanCode, normalizedCycle)?.name ?? null;
  };

  const getButtonState = (
    planCode: SelectablePlanCode | 'ENTERPRISE' | null,
    pricingTier?: PricingTierOption,
  ) => {
    const asState = (buttonText: string, isDisabled: boolean) => ({
      buttonText: getLocalizedButtonText(buttonText),
      actionLabel: buttonText,
      isDisabled,
    });

    if (planCode === 'ENTERPRISE') {
      return asState('Contact Sales', false);
    }

    const configuredPlan = planCode === null ? null : getPlanByCodeAndCycle(planCode, billingCycle);
    const trialStartSubscriptionState = getTrialStartSubscriptionCardState({
      requiresCheckout: requiresCheckoutForTrialSelection,
      isPurchasable: Boolean(configuredPlan && !isFallbackPlan(configuredPlan)),
    });
    if (trialStartSubscriptionState) {
      return asState(trialStartSubscriptionState.actionLabel, trialStartSubscriptionState.isDisabled);
    }

    if (planCode === null) {
      return pricingTier
        ? asState(resolveFrontendPricingTierActionLabel(pricingTier, effectiveCurrentPlanCode), false)
        : asState('Unavailable', true);
    }

    if (!configuredPlan) {
      return asState('Unavailable', true);
    }

    const isCurrentPlan = effectiveCurrentPlanCode === planCode;

    if (isShopifyBilling && isShopifySubscriptionCanceled && isCurrentPlan && effectiveCurrentBillingCycle === billingCycle) {
      return asState('Resubscribe', false);
    }

    if (isExpired) {
      const expiredButtonText = fallbackTrialWorkspace ? 'Start subscription' : resolveExpiredPlanCardButtonText(planCode);
      return asState(expiredButtonText, false);
    }

    const scheduledButtonState = resolveScheduledPlanButtonState({
      scheduledChange: scheduledChangeStatus,
      currentPlanCode: effectiveCurrentPlanCode,
      currentBillingCycle: effectiveCurrentBillingCycle,
      planCode,
      billingCycle,
    });
    if (scheduledButtonState) {
      return asState(scheduledButtonState.buttonText, scheduledButtonState.isDisabled);
    }

    if (shouldUseTrialUi) {
      const selectedPlanCode = (scheduledPlanChange?.planCode ?? currentPlanCode) as SelectablePlanCode;
      const selectedCycle = scheduledPlanChange?.billingCycle ?? userBillingCycle;

      const actionLabel = getTrialPlanCardAction({
        currentPlanCode: currentPlanCode as SelectablePlanCode,
        currentCycle: userBillingCycle,
        selectedPlanCode,
        selectedCycle,
        planCode,
        cycle: billingCycle,
        requiresCheckout: requiresCheckoutForTrialSelection,
      });
      return {
        buttonText: getLocalizedButtonText(actionLabel),
        actionLabel,
        isDisabled: false,
      };
    }

    if (shouldShowActivateForCard(planCode, billingCycle)) {
      return asState('Activate Paid Plan', false);
    }

    const isTeamToTeamPlanSwitch = isTeamPlanCode(effectiveCurrentPlanCode) && isTeamPlanCode(planCode) && effectiveCurrentPlanCode !== planCode;
    if (isTeamToTeamPlanSwitch) {
      return asState('Switch plan', false);
    }

    const activePlanChangeIntent = resolvePlanChangeIntent({
      currentPlanCode: effectiveCurrentPlanCode,
      currentCycle: effectiveCurrentBillingCycle,
      targetPlanCode: planCode,
      targetCycle: billingCycle,
      renew: false,
      isExpired: false,
    });
    if (activePlanChangeIntent.isDowngradingSoloVariant) {
      return asState('Downgrade', false);
    }

    const summaryState = getSummaryCardState(planCode, billingCycle);
    if (!shouldBypassSummaryCardState && summaryState?.buttonText) {
      return {
        buttonText: getLocalizedButtonText(summaryState.buttonText),
        actionLabel: summaryState.buttonText,
        isDisabled: Boolean(summaryState.disabled),
      };
    }

    if (isRenewableCurrentPlanCard(planCode, billingCycle)) {
      return asState('Renew', false);
    }

    if (effectiveCurrentPlanCode && !isSoloPlanCode(effectiveCurrentPlanCode) && isSoloPlanCode(planCode)) {
      return asState('Switch to Solo', false);
    }

    if (isCurrentPlan && effectiveCurrentBillingCycle === billingCycle) {
      return asState('Current Plan', true);
    }

    if (isCurrentPlan && effectiveCurrentBillingCycle === 'monthly' && billingCycle === 'yearly') {
      return asState('Switch to Yearly', false);
    }

    if (isCurrentPlan && effectiveCurrentBillingCycle === 'yearly' && billingCycle === 'monthly') {
      return asState('Switch to Monthly', false);
    }

    if (isTeamPlanCode(planCode)) {
      return asState('Switch to Team', false);
    }

    return asState('Upgrade', false);
  };

  const confirmFrontendTierSwitch = async (tier: PricingTierOption): Promise<void> => {
    if (!user) {
      openSignInModal();
      return;
    }

    const targetPlanName = t('website.pricing.frontendTierPlanName', {
      plan: tier.group === 'team' ? t('website.pricing.teamName') : t('website.pricing.soloName'),
      value: numberFormatter.format(getTierWorkUnits(tier) ?? 0),
    });
    const currentTier = getDynamicPricingTierForPlanCode('solo', effectiveCurrentPlanCode)
      ?? getDynamicPricingTierForPlanCode('team', effectiveCurrentPlanCode);
    const currentPrice = currentTier
      ? getPricingTierPrice(currentTier, effectiveCurrentBillingCycle)
      : null;
    const targetPrice = getPricingTierPrice(tier, billingCycle);
    const isDowngrade = currentPrice !== null && targetPrice < currentPrice;

    const confirmed = await showDialog(isDowngrade
      ? {
          title: t('website.pricing.downgradeToPlanTitle', { plan: targetPlanName }),
          subtitle: (
            <>
              {t('website.pricing.downgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{targetPlanName}</strong> {t('website.pricing.downgradeToPlanDescriptionSuffix')}
            </>
          ),
          confirmText: t('website.pricing.confirmChange'),
          cancelText: t('common.cancel'),
        }
      : {
          title: t('website.pricing.upgradeToPlanTitle', { plan: targetPlanName }),
          subtitle: (
            <>
              {t('website.pricing.upgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{targetPlanName}</strong>{' '}
              {t('website.pricing.upgradeToPlanDescriptionMiddle', {
                cycle: billingCycle === 'monthly' ? t('website.pricing.monthlyLower') : t('website.pricing.yearlyLower'),
              })}{' '}
              {t('website.pricing.upgradeToPlanDescriptionSuffix')}
            </>
          ),
          confirmText: t('website.pricing.confirmChange'),
          cancelText: t('common.cancel'),
        });

    if (!confirmed) return;
    // Backend handoff: assigning tier.planCode routes confirmed changes through createOrSwitchSubscription.
  };

  const triggerTrialActivation = async (planCode: SelectablePlanCode, cycle: BillingCycle) => {
    if (!workspaceId) {
      showToast(t('website.pricing.noWorkspaceSelected'), 'error');
      return;
    }
    const targetPlan = await ensureRealPlan(planCode, cycle);
    if (!targetPlan) {
      showToast(t('website.pricing.planNotFound'), 'error');
      return;
    }

    const localizedPlanName = getLocalizedPlanName(targetPlan.code, targetPlan.name);
    const confirmed = await showDialog({
      title: t('website.pricing.activatePaidPlanConfirmTitle', { plan: localizedPlanName }),
      subtitle: (
        <>
          {t('website.pricing.activatePaidPlanConfirmDescriptionPrefix')} <strong className="text-text-main">{localizedPlanName}</strong>{' '}
          {t('website.pricing.activatePaidPlanConfirmDescriptionMiddle', {
            cycle: cycle === 'monthly' ? t('website.pricing.monthlyLower') : t('website.pricing.yearlyLower'),
          })}{' '}
          {t('website.pricing.activatePaidPlanConfirmDescriptionSuffix')}
        </>
      ),
      confirmText: t('website.pricing.activatePaidPlan'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;

    setLoadingPlanName(getPricingCardLoadingName(planCode));
    try {
      logSentryEvent('payment.trial_activation_checkout.started', {
        area: 'payment',
        workspace_id: workspaceId,
        plan_code: planCode,
        billing_cycle: cycle,
      });
      const checkoutUrls = buildCheckoutReturnUrls({
        currentUrl: window.location.href,
        mode: 'upgrade',
        planCode: targetPlan.code,
        planName: targetPlan.name,
        billingCycle: cycle,
        workspaceId,
      });

      const checkoutResponse = await billingService.createSubscription({
        planId: targetPlan.id,
        successUrl: checkoutUrls.successUrl,
        cancelUrl: checkoutUrls.cancelUrl,
        allowTrial: false,
        axiosRequestConfig: { headers: { 'X-Workspace-Id': workspaceId } },
      });

      const checkoutResult = resolveTrialActivationCheckoutResult(checkoutResponse);

      if (checkoutResult.kind === 'redirect') {
        logSentryEvent('payment.checkout.redirect_started', {
          area: 'payment',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          billing_cycle: cycle,
          flow: 'trial_activation',
        });
        setScheduledPlanChange(null);
        setScheduledChangeStatus(null);
        window.location.href = checkoutResult.paymentUrl;
        return;
      }

      if (checkoutResult.kind === 'completed') {
        setScheduledPlanChange(null);
        setScheduledChangeStatus(null);
        const paidActivationReturnUrl = buildPaymentReturnUrl({
          origin: window.location.origin,
          kind: 'subscription',
          result: 'success',
          mode: 'upgrade',
          returnPath: PAYMENT_RETURN_UPGRADE_PATH,
          workspaceId,
          planCode: targetPlan.code,
          planName: targetPlan.name,
          billingCycle: cycle,
        });
        logSentryEvent('payment.trial_activation.completed', {
          area: 'payment',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          billing_cycle: cycle,
        });
        window.location.href = paidActivationReturnUrl;
        return;
      }

      logSentryEvent(
        'payment.trial_activation_checkout.failed',
        {
          area: 'payment',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          reason: 'missing_payment_url',
        },
        'warn',
      );
      captureSentryError(new Error('Failed to start paid checkout'), {
        area: 'payment',
        flow: 'trial_activation',
        workspace_id: workspaceId,
        plan_id: targetPlan.id,
        plan_code: targetPlan.code,
        reason: 'missing_payment_url',
      });
      showToast(t('website.pricing.failedToStartPaidCheckout'), 'error');
    } catch (err) {
      logSentryEvent(
        'payment.trial_activation_checkout.failed',
        {
          area: 'payment',
          workspace_id: workspaceId,
          plan_code: planCode,
        },
        'error',
      );
      captureSentryError(err, {
        area: 'payment',
        flow: 'trial_activation',
        workspace_id: workspaceId,
        plan_code: planCode,
        billing_cycle: cycle,
      });
      showToast(t('website.pricing.failedToStartPaidCheckout'), 'error');
      console.error(err);
    } finally {
      setLoadingPlanName(undefined);
    }
  };

  const createOrSwitchSubscription = async (planCode: SelectablePlanCode, cycle: BillingCycle, renew: boolean, actionLabel?: string) => {
    if (!user) {
      openSignInModal();
      return;
    }

    const targetPlan = await ensureRealPlan(planCode, cycle);
    if (!targetPlan) {
      showToast(t('website.pricing.planNotFound'), 'error');
      return;
    }

    const planChangeIntent = resolvePlanChangeIntent({
      currentPlanCode,
      currentCycle: userBillingCycle,
      targetPlanCode: planCode,
      targetCycle: cycle,
      renew,
      isExpired,
    });
    const isSwitchingCycle = planChangeIntent.isSwitchingCycle;
    const isDowngradingToBasicByState = planChangeIntent.isDowngradingToBasic;
    const normalizedActionLabel = typeof actionLabel === 'string' ? actionLabel.toLowerCase() : '';
    const isDowngradingToBasicByAction = !renew && !isExpired && isSoloPlanCode(planCode) && (normalizedActionLabel.includes('switch to solo') || normalizedActionLabel.includes('switch to basic'));
    const isDowngradingTeamVariant = planChangeIntent.isDowngradingTeamVariant;
    const isDowngradingToBasic = Boolean(isDowngradingToBasicByState || isDowngradingToBasicByAction);
    const isDowngradingPlan = isDowngradingToBasic || isDowngradingTeamVariant;
    const shouldConfirmPaidPlanChange = !isTrialWorkspace;
    const isUpgradingToPro = planChangeIntent.isUpgradingToPro || planChangeIntent.isUpgradingTeamVariant;
    const isDeferredPlanChange = planChangeIntent.isDeferredPlanChange || isDowngradingToBasicByAction;
    const shouldForceCheckoutForTrialSelection = requiresCheckoutForTrialSelection && isSelectablePlanCode(planCode);
    const shouldRenewRequest = renew && !isExpired;
    const shouldConfirmShopifyResubscribe = isShopifyBilling && normalizedActionLabel === 'resubscribe';

    if (shouldConfirmShopifyResubscribe) {
      const confirmed = await showDialog({
        title: t('website.pricing.resubscribeTitle'),
        subtitle: t('website.pricing.resubscribeDescription'),
        confirmText: t('website.pricing.confirmResubscribe'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
      showToast(t('website.pricing.openingShopifySubscriptionApprovalToast'), 'info');
    }

    if (shouldConfirmPaidPlanChange && isUpgradingToPro) {
      const cycleLabel = cycle === 'monthly' ? 'monthly' : 'yearly';
      const confirmed = await showDialog({
        title: t('website.pricing.upgradeToPlanTitle', {
          plan: getLocalizedPlanName(targetPlan.code, targetPlan.name),
        }),
        subtitle: (
          <>
            {t('website.pricing.upgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{getLocalizedPlanName(targetPlan.code, targetPlan.name)}</strong>{' '}
            {t('website.pricing.upgradeToPlanDescriptionMiddle', {
              cycle: cycleLabel === 'monthly' ? t('website.pricing.monthlyLower') : t('website.pricing.yearlyLower'),
            })}{' '}
            {t('website.pricing.upgradeToPlanDescriptionSuffix')}
          </>
        ),
        confirmText: t('website.pricing.confirmChange'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
    }

    if (shouldConfirmPaidPlanChange && isDowngradingPlan) {
      const confirmed = await showDialog({
        title: t('website.pricing.downgradeToPlanTitle', {
          plan: getLocalizedPlanName(targetPlan.code, targetPlan.name),
        }),
        subtitle: (
          <>
            {t('website.pricing.downgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{getLocalizedPlanName(targetPlan.code, targetPlan.name)}</strong> {t('website.pricing.downgradeToPlanDescriptionSuffix')}
          </>
        ),
        confirmText: t('website.pricing.confirmChange'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
    }

    if (shouldConfirmPaidPlanChange && isSwitchingCycle) {
      const targetCycleLabel = cycle === 'monthly' ? 'Monthly' : 'Yearly';
      const currentCycleLower = userBillingCycle === 'monthly' ? 'monthly' : 'yearly';
      const confirmed = await showDialog({
        title: t('website.pricing.switchBillingTitle', {
          cycle: targetCycleLabel === 'Monthly' ? t('website.pricing.monthly') : t('website.pricing.yearly'),
        }),
        subtitle: t('website.pricing.switchBillingDescription', {
          cycle: targetCycleLabel === 'Monthly' ? t('website.pricing.monthlyLower') : t('website.pricing.yearlyLower'),
          currentCycle: currentCycleLower === 'monthly' ? t('website.pricing.monthlyLower') : t('website.pricing.yearlyLower'),
        }),
        confirmText: t('website.pricing.confirmChange'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
    }

    if (shouldConfirmPaidPlanChange && shouldRenewRequest) {
      const confirmed = await showDialog({
        title: t('website.pricing.renewSubscription'),
        subtitle: (
          <>
            {t('website.pricing.renewSubscriptionDescriptionPrefix')} <strong className="text-text-main">{t('website.pricing.autoRenewal')}</strong> {t('website.pricing.renewSubscriptionDescriptionSuffix')}
          </>
        ),
        confirmText: t('website.pricing.confirmRenewal'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
    }

    setLoadingPlanName(getPricingCardLoadingName(planCode));

    try {
      logSentryEvent('payment.subscription.requested', {
        area: 'payment',
        workspace_id: workspaceId,
        mode,
        plan_id: targetPlan.id,
        plan_code: targetPlan.code,
        billing_cycle: cycle,
        renew: shouldRenewRequest,
        force_checkout: shouldForceCheckoutForTrialSelection,
        action_label: actionLabel,
      });

      const checkoutUrls = buildCheckoutReturnUrls({
        currentUrl: window.location.href,
        mode: 'upgrade',
        planCode: targetPlan.code,
        planName: targetPlan.name,
        billingCycle: cycle,
        workspaceId,
      });

      const requestPayload = {
        planId: targetPlan.id,
        successUrl: checkoutUrls.successUrl,
        cancelUrl: checkoutUrls.cancelUrl,
        axiosRequestConfig: { headers: { 'X-Workspace-Id': workspaceId } },
      };

      if (shouldForceCheckoutForTrialSelection) {
        const checkoutResponse = await billingService.createSubscription(requestPayload);
        if (checkoutResponse.success && checkoutResponse.paymentUrl) {
          logSentryEvent('payment.checkout.redirect_started', {
            area: 'payment',
            workspace_id: workspaceId,
            plan_id: targetPlan.id,
            plan_code: targetPlan.code,
            billing_cycle: cycle,
            flow: 'trial_checkout_required',
          });
          setScheduledPlanChange(null);
          setScheduledChangeStatus(null);
          window.location.href = checkoutResponse.paymentUrl;
          return;
        }

        logSentryEvent(
          'payment.checkout.failed',
          {
            area: 'payment',
            workspace_id: workspaceId,
            plan_id: targetPlan.id,
            plan_code: targetPlan.code,
            flow: 'trial_checkout_required',
            reason: 'missing_payment_url',
          },
          'warn',
        );
        captureSentryError(new Error('Failed to start checkout'), {
          area: 'payment',
          flow: 'trial_checkout_required',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          reason: 'missing_payment_url',
        });
        showToast(t('website.pricing.failedToStartCheckout'), 'error');
        return;
      }

      const response = shouldRenewRequest ? await billingService.renewSubscription(requestPayload) : await billingService.createSubscription(requestPayload);

      if (response.success && response.paymentUrl) {
        logSentryEvent('payment.checkout.redirect_started', {
          area: 'payment',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          billing_cycle: cycle,
          renew: shouldRenewRequest,
          flow: 'subscription',
        });
        setScheduledPlanChange(null);
        window.location.href = response.paymentUrl;
        return;
      }

      if (response.success) {
        const effectiveMode = resolveEffectiveMode(response);
        const shouldTreatAsDeferred = effectiveMode === 'next_cycle' || (effectiveMode === null && (isDeferredPlanChange || (isTrialWorkspace && !shouldRenewRequest)));

        if (shouldTreatAsDeferred) {
          setScheduledPlanChange({ planCode, billingCycle: cycle });
          setScheduledChangeStatus({
            changeType: typeof response.action === 'string' ? response.action : null,
            effectiveMode: 'next_cycle',
            effectiveAt: typeof response.effectiveAt === 'string' ? response.effectiveAt : null,
            fromPlanCode: effectiveCurrentPlanCode || null,
            fromPlanName: getPlanDisplayName(effectiveCurrentPlanCode, effectiveCurrentBillingCycle) || userBilling.summary?.planName || userPlan?.planCode || null,
            fromBillingCycle: effectiveCurrentBillingCycle || null,
            toPlanCode: planCode,
            toPlanName: targetPlan.name || getPlanDisplayName(planCode, cycle) || null,
            toBillingCycle: cycle,
          });
          if (isTrialWorkspace) {
            setTrialActivationStatus((prev) => ({
              ...prev,
              loaded: true,
              canActivatePaidNow: isImmediateTrialActivationSelection({
                currentPlanCode: currentPlanCode as SelectablePlanCode,
                currentCycle: userBillingCycle,
                selectedPlanCode: planCode,
                selectedCycle: cycle,
              }),
              isInTrial: true,
            }));
          }
        } else {
          setScheduledPlanChange(null);
          setScheduledChangeStatus(null);
        }

        if (isTrialWorkspace && shouldTreatAsDeferred) {
          showToast(t('website.pricing.trialPlanUpdatedToast'), 'success');
        } else if (isDeferredPlanChange || shouldTreatAsDeferred) {
          showToast(t('website.pricing.planChangeScheduledToast'), 'success');
        } else if (isUpgradingToPro) {
          showToast(t('website.pricing.planUpgradedToast'), 'success');
        } else if (shouldRenewRequest) {
          if (profile && setProfile && profile.workspacePlan) {
            setProfile({
              ...profile,
              workspacePlan: {
                ...profile.workspacePlan,
                status: typeof response.status === 'string' ? response.status : 'active',
                cancelAtPeriodEnd: false,
                actionLabel: t('website.pricing.manage'),
                primaryAction: t('website.pricing.manage'),
              },
            });
          }
          showToast(t('website.pricing.subscriptionRenewedToast'), 'success');
        } else {
          showToast(t('website.pricing.subscriptionCreatedToast'), 'success');
        }

        // Only apply optimistic plan update for true immediate tier upgrades.
        if (mode === 'upgrade' && isUpgradingToPro && effectiveMode === 'immediate' && !shouldTreatAsDeferred && profile && setProfile && profile.workspacePlan) {
          setProfile({
            ...profile,
            workspacePlan: {
              ...profile.workspacePlan,
              status: 'active',
              billingCycle: cycle,
              planCode: targetPlan.code,
              planName: targetPlan.name,
              planId: targetPlan.id,
            },
          });
        }
        await Promise.all([userBilling.refreshAll(), refreshWorkspaces({ silent: true })]);
        logSentryEvent('payment.subscription.updated', {
          area: 'payment',
          workspace_id: workspaceId,
          plan_id: targetPlan.id,
          plan_code: targetPlan.code,
          billing_cycle: cycle,
          renew: shouldRenewRequest,
          effective_mode: effectiveMode,
          deferred: shouldTreatAsDeferred,
        });
      }
    } catch (err) {
      logSentryEvent(
        'payment.subscription.failed',
        {
          area: 'payment',
          workspace_id: workspaceId,
          mode,
          plan_code: planCode,
          billing_cycle: cycle,
          renew: shouldRenewRequest,
        },
        'error',
      );
      captureSentryError(err, {
        area: 'payment',
        flow: 'subscription',
        workspace_id: workspaceId,
        mode,
        plan_code: planCode,
        billing_cycle: cycle,
        renew: shouldRenewRequest,
      });
      showToast(
        shouldRenewRequest
          ? t('website.pricing.failedToRenewSubscription')
          : t('website.pricing.failedToCreateSubscription'),
        'error',
      );
      console.error(err);
    } finally {
      setLoadingPlanName(undefined);
    }
  };

  const handleCancelSubscription = () => {
    logSentryEvent('payment.portal.requested', {
      area: 'payment',
      workspace_id: workspaceId,
    });
    billingService
      .createSubscriptionPortal(window.location.href)
      .then((response) => {
        if (response.paymentType === 'shopify') {
          logSentryEvent('payment.portal.shopify_reload', {
            area: 'payment',
            workspace_id: workspaceId,
          });
          window.location.reload();
          return;
        }
        if (response.url) {
          logSentryEvent('payment.portal.redirect_started', {
            area: 'payment',
            workspace_id: workspaceId,
            payment_type: response.paymentType,
          });
          window.location.href = response.url;
        } else {
          logSentryEvent(
            'payment.portal.failed',
            {
              area: 'payment',
              workspace_id: workspaceId,
              reason: 'missing_url',
            },
            'warn',
          );
          captureSentryError(new Error('Failed to open billing portal'), {
            area: 'payment',
            flow: 'billing_portal',
            workspace_id: workspaceId,
            reason: 'missing_url',
          });
          showToast(t('website.pricing.failedToOpenBillingPortal'), 'error');
        }
      })
      .catch((error) => {
        logSentryEvent(
          'payment.portal.failed',
          {
            area: 'payment',
            workspace_id: workspaceId,
          },
          'error',
        );
        captureSentryError(error, {
          area: 'payment',
          flow: 'billing_portal',
          workspace_id: workspaceId,
        });
        showToast(t('website.pricing.failedToOpenBillingPortal'), 'error');
        console.error(error);
      });
  };

  const handleShopifyCancelSubscription = async () => {
    const isPendingPaymentCancellation = shopifyCancellationCopyState === 'pending_payment';
    const confirmed = await showDialog({
      title: t('website.pricing.cancelSubscriptionTitle'),
      subtitle: isPendingPaymentCancellation ? t('website.pricing.cancelSubscriptionPendingPaymentDescription') : t('website.pricing.cancelSubscriptionActivePaidDescription'),
      confirmText: t('website.pricing.confirmCancelSubscription'),
      cancelText: t('common.cancel'),
    });

    if (!confirmed) return;

    try {
      setIsCancelingSubscription(true);
      const response = await billingService.cancelSubscription({
        headers: { 'X-Workspace-Id': workspaceId },
      });

      if (response?.success === false) {
        throw new Error(response.message || 'Failed to cancel subscription');
      }

      const returnedWorkspacePlan = response?.workspacePlan || response?.workspace_plan || null;
      if (returnedWorkspacePlan) {
        userBilling.applyWorkspacePlan(returnedWorkspacePlan as any);
        if (profile && setProfile) {
          setProfile({
            ...profile,
            workspacePlan: returnedWorkspacePlan as any,
          });
        }
      } else if (response?.subscription && profile?.workspacePlan && setProfile) {
        const nextWorkspacePlan = {
          ...profile.workspacePlan,
          status: response.subscription.status || response.status || profile.workspacePlan.status,
          cancelAtPeriodEnd: response.subscription.cancelAtPeriodEnd ?? response.subscription.cancel_at_period_end ?? profile.workspacePlan.cancelAtPeriodEnd,
          actionLabel: response.subscription.actionLabel || profile.workspacePlan.actionLabel,
          primaryAction: response.subscription.primaryAction || profile.workspacePlan.primaryAction,
        };
        userBilling.applyWorkspacePlan(nextWorkspacePlan as any);
        setProfile({
          ...profile,
          workspacePlan: nextWorkspacePlan as any,
        });
      }

      await Promise.all([userBilling.refreshAll(), refreshWorkspaces({ silent: true, preferredWorkspaceId: workspaceId })]);
      showToast(isPendingPaymentCancellation ? t('website.pricing.subscriptionPendingPaymentCanceledToast') : t('website.pricing.subscriptionActivePaidCanceledToast'), 'success');
      logSentryEvent('payment.shopify_subscription.canceled', {
        area: 'payment',
        workspace_id: workspaceId,
        copy_state: shopifyCancellationCopyState,
        response_status: response?.status || response?.subscription?.status || null,
      });
    } catch (error) {
      logSentryEvent(
        'payment.shopify_subscription.cancel_failed',
        {
          area: 'payment',
          workspace_id: workspaceId,
          copy_state: shopifyCancellationCopyState,
        },
        'error',
      );
      captureSentryError(error, {
        area: 'payment',
        flow: 'shopify_subscription_cancel',
        workspace_id: workspaceId,
      });
      showToast(t('website.pricing.failedToCancelSubscription'), 'error');
      console.error(error);
    } finally {
      setIsCancelingSubscription(false);
    }
  };

  const billingFooterActionLabel = isShopifyBilling ? t('website.pricing.cancelSubscription') : t('website.pricing.editBilling');

  const handleBillingFooterAction = () => {
    if (isCancelingSubscription) return;

    if (isShopifyBilling) {
      void handleShopifyCancelSubscription();
      return;
    }

    handleCancelSubscription();
  };

  const tierSelectDisabled = disabled || Boolean(loadingPlanName);
  const soloTierSelector = (
    <PricingTierSelect
      ariaLabel={t('website.pricing.selectSoloTier')}
      options={getTierSelectorOptions(pricingTierCatalog.soloTiers)}
      selectedId={selectedSoloTier.id}
      onSelect={(tierId) => {
        setSelectedSoloTierId(tierId);
        setHasManualSoloTierSelection(true);
      }}
      disabled={tierSelectDisabled}
    />
  );
  const teamTierSelector = (
    <PricingTierSelect
      ariaLabel={t('website.pricing.selectTeamTier')}
      options={getTierSelectorOptions(pricingTierCatalog.teamTiers)}
      selectedId={selectedTeamTier.id}
      onSelect={setManualTeamTierId}
      disabled={tierSelectDisabled}
    />
  );
  const selectedTeamCardPlanCode = selectedTeamTier.planCode;

  const resolveCardBubbleText = (
    planCode: SelectablePlanCode | 'ENTERPRISE' | null,
    pricingTier?: PricingTierOption,
  ): string | null => {
    if (planCode === null) {
      return requiresCheckoutForTrialSelection && pricingTier
        ? t('website.pricing.previewTierUnavailableDescription')
        : null;
    }
    if (planCode === 'ENTERPRISE') {
      return null;
    }
    const configuredPlan = getPlanByCodeAndCycle(planCode, billingCycle);
    if (!configuredPlan || (requiresCheckoutForTrialSelection && isFallbackPlan(configuredPlan))) {
      return t('website.pricing.configuredTierUnavailableDescription');
    }

    const bubbleState = resolveScheduledPlanBubbleState({
      scheduledChange: scheduledChangeStatus,
      currentPlanCode: effectiveCurrentPlanCode,
      currentBillingCycle: effectiveCurrentBillingCycle,
      planCode,
      billingCycle,
    });
    if (!bubbleState) return null;

    const effectiveDate = formatScheduledEffectiveDate(bubbleState.effectiveAt, locale);
    if (!effectiveDate) return null;
    const localizedPlanLabel = bubbleState.billingCycle
      ? t('website.pricing.planWithBillingCycle', {
          plan: bubbleState.planLabel,
          cycle: bubbleState.billingCycle === 'yearly'
            ? t('website.pricing.yearly')
            : t('website.pricing.monthly'),
        })
      : bubbleState.planLabel;

    return bubbleState.kind === 'current-plan'
      ? t('website.pricing.currentPlanActiveUntil', {
          plan: localizedPlanLabel,
          date: effectiveDate,
        })
      : t('website.pricing.scheduledPlanTakesEffectOn', {
          plan: localizedPlanLabel,
          date: effectiveDate,
        });
  };

  const uiPlans: Array<
    PlanData & {
      cardKey: 'BASIC' | 'TEAM' | 'ENTERPRISE';
      planCode: SelectablePlanCode | 'ENTERPRISE' | null;
      pricingTier?: PricingTierOption;
    }
  > = [
    (() => {
      return {
        cardKey: 'BASIC' as const,
        planCode: selectedSoloTier.planCode,
        pricingTier: selectedSoloTier,
        name: t('website.pricing.soloName'),
        price: getTierDisplayedPrice(selectedSoloTier),
        description: t('website.pricing.soloDescription'),
        buttonText: 'Upgrade',
        isPopular: false,
        tierSelector: soloTierSelector,
        featureMode: 'grouped' as const,
        featureGroups: getTierFeatureGroups(selectedSoloTier),
        builtInCapabilitiesLabel: t('website.pricing.builtInAiCapabilities'),
        builtInModels: builtInAiModels,
        estimatedCapacity: getTierEstimatedCapacity(selectedSoloTier),
        showIntegrations: true,
      };
    })(),
    (() => {
      return {
        cardKey: 'TEAM' as const,
        planCode: selectedTeamCardPlanCode,
        pricingTier: selectedTeamTier,
        name: t('website.pricing.teamName'),
        price: getTierDisplayedPrice(selectedTeamTier),
        description: t('website.pricing.teamDescription'),
        buttonText: 'Upgrade',
        isPopular: false,
        tierSelector: teamTierSelector,
        featureMode: 'grouped' as const,
        featureGroups: getTierFeatureGroups(selectedTeamTier),
        builtInCapabilitiesLabel: t('website.pricing.builtInAiCapabilities'),
        builtInModels: builtInAiModels,
        estimatedCapacity: getTierEstimatedCapacity(selectedTeamTier),
        highlightLimits: true,
        showIntegrations: true,
      };
    })(),
    {
      cardKey: 'ENTERPRISE',
      planCode: 'ENTERPRISE',
      name: t('website.pricing.scaleName'),
      price: 'Custom',
      description: t('website.pricing.scaleDescription'),
      buttonText: 'Contact Sales',
      isPopular: false,
      isCustom: true,
      featureMode: 'flat',
      features: [t('website.pricing.scaleFeatureOne'), t('website.pricing.scaleFeatureTwo'), t('website.pricing.scaleFeatureThree'), t('website.pricing.scaleFeatureFour'), t('website.pricing.scaleFeatureFive')],
      builtInCapabilitiesLabel: t('website.pricing.builtInAiCapabilities'),
      builtInModels: builtInAiModels,
      estimatedCapacity: t('website.pricing.estimatedScaleOperations'),
    },
  ];

  const showCancelOrPortal = mode === 'upgrade' && isSelectablePlanCode(currentPlanCode) && !requiresCheckoutForTrialSelection && !(isShopifyBilling && isShopifySubscriptionCanceled);
  const shouldShowPricingSkeleton = mode === 'upgrade' && (fetchingPlans || Boolean(user && workspaceId && !trialActivationStatus.loaded));
  return (
    <>
      <div className={clsx('w-full flex flex-col', className)}>
        <div className="flex justify-center mb-7">
          <div className="inline-flex items-center rounded-full border border-[#E8E5E0] bg-[#F5F4F1] p-0.5 shadow-none">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={clsx(
                'relative z-10 flex min-w-[104px] items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 border',
                billingCycle === 'monthly' ? 'bg-white text-[#292723] shadow-[0_2px_5px_rgba(33,31,27,0.06)] border-[#E3E0DA]' : 'text-[#77736D] hover:text-[#3F3C37] border-transparent',
              )}
            >
              {t('website.pricing.monthly')}
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={clsx(
                'relative z-10 flex min-w-[134px] items-center justify-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 border',
                billingCycle === 'yearly' ? 'bg-white text-[#292723] shadow-[0_2px_5px_rgba(33,31,27,0.06)] border-[#E3E0DA]' : 'text-[#77736D] hover:text-[#3F3C37] border-transparent',
              )}
            >
              <span>{t('website.pricing.yearly')}</span>
              <span className="text-[10px] font-bold text-[#2F7BFF] whitespace-nowrap">
                {` · ${t('website.pricing.tenPercentOff')}`}
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch max-w-[820px] mx-auto w-full">
          {uiPlans.filter(p => p.cardKey !== 'ENTERPRISE').map((plan) => {
            const { buttonText, actionLabel, isDisabled: planIsDisabled } = getButtonState(plan.planCode, plan.pricingTier);
            const bubbleText = resolveCardBubbleText(plan.planCode, plan.pricingTier);

            return (
              <PricingCard
                key={plan.cardKey}
                plan={{ ...plan, buttonText, bubbleText }}
                loadingPlanName={loadingPlanName}
                fetching={shouldShowPricingSkeleton}
                disabled={planIsDisabled || disabled || Boolean(loadingPlanName)}
                onAction={() => {
                  if (plan.planCode === null) {
                    if (plan.pricingTier) {
                      void confirmFrontendTierSwitch(plan.pricingTier);
                    }
                    return;
                  }
                  if (plan.planCode === 'ENTERPRISE') {
                    return;
                  }

                  const shouldActivatePaidNow = shouldShowActivateForCard(plan.planCode, billingCycle);
                  if (shouldActivatePaidNow) {
                    triggerTrialActivation(plan.planCode, billingCycle);
                    return;
                  }

                  const shouldRenew = isRenewableCurrentPlanCard(plan.planCode, billingCycle);
                  createOrSwitchSubscription(plan.planCode, billingCycle, shouldRenew, actionLabel);
                }}
              />
            );
          })}
        </div>

        {(() => {
          const enterprisePlan = uiPlans.find((p) => p.cardKey === 'ENTERPRISE');
          if (!enterprisePlan) return null;
          return (
            <div className="mt-5 max-w-[820px] mx-auto w-full px-0.5">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setIsContactSalesOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsContactSalesOpen(true);
                  }
                }}
                className="relative flex items-center justify-between gap-6 rounded-[20px] border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1A162F]/8 border border-[#1A162F]/15">
                    <Buildings size={20} className="text-[#1A162F]" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="text-[15px] font-bold text-text-main leading-tight">
                      {enterprisePlan.name}
                    </div>
                    <div className="text-xs font-medium text-[#706C66] leading-relaxed">
                      {enterprisePlan.description}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsContactSalesOpen(true);
                  }}
                  className="shrink-0 inline-flex min-h-[38px] items-center justify-center rounded-[10px] border border-[#ECEAE6] bg-[#F8F7F5] px-4 text-xs font-semibold text-text-main transition-colors duration-150 hover:border-[#DFDCD6] hover:bg-[#F4F3F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8D5CF]/80 focus-visible:ring-offset-2"
                >
                  {getLocalizedButtonText(enterprisePlan.buttonText || 'Contact Sales')}
                </button>
              </div>
            </div>
          );
        })()}

        {showCancelOrPortal ? (
          <div className="max-w-[820px] mx-auto w-full px-0.5 flex justify-center mt-5">
            <button
              type="button"
              onClick={handleBillingFooterAction}
              disabled={isCancelingSubscription}
              className={clsx(
                'text-sm text-text-sub underline transition-colors hover:text-text-main focus-visible:outline-none',
                isCancelingSubscription ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              )}
            >
              {billingFooterActionLabel}
            </button>
          </div>
        ) : null}

        <PlanComparison
          tiers={pricingTierCatalog.allTiers}
          billingCycle={billingCycle}
        />
      </div>

      <ContactSalesModal isOpen={isContactSalesOpen} onClose={() => setIsContactSalesOpen(false)} />
    </>
  );
};

export default PlanSelector;
